import React, { useEffect, useRef, useState } from "react";
import { Header, Nav } from "rsuite";
import { useLocation } from "react-router-dom";
import eraLogoPath from "../../../../img/rinf-new-logo.png";
import { getEnvStyle } from "../../EnvBanner.js";
import { UserMenu } from "../../../Authentication/UserMenu.js";
import { NARROW_VIEWPORT_QUERY } from "../breakpoints";
/*
 * 74px is --redesign-header-height in RedesignShell.css. The header stays
 * shown for the first two of those, and only hides after a further downward
 * move past this threshold, so a slow or jittery scroll does not flicker.
 */
const HEADER_HEIGHT_PX = 74;
const ALWAYS_SHOWN_BELOW_PX = HEADER_HEIGHT_PX * 2;
const HIDE_AFTER_PX = 10;
const AUTO_HIDE_QUERY = `${NARROW_VIEWPORT_QUERY} and (min-height: 500px)`;
export default function RedesignHeader({ auth, userInitials, activeRole, enabledExtras, isAuthenticated, userInfo, userRoles, userRolesCompany, setRole, menuButton, }) {
    const location = useLocation();
    const [hidden, setHidden] = useState(false);
    const hiddenRef = useRef(false);
    const routeRef = useRef(location.pathname);
    const userMenuRef = useRef(null);
    /*
     * Reset while rendering, not in an effect. An effect runs after paint, so
     * the next route would flash one frame of the hidden header. Same-component
     * setState during render is how React adjusts state when a prop changes.
     */
    if (routeRef.current !== location.pathname) {
        routeRef.current = location.pathname;
        hiddenRef.current = false;
        if (hidden)
            setHidden(false);
    }
    useEffect(() => {
        const media = window.matchMedia(AUTO_HIDE_QUERY);
        /*
         * anchor / previous are the scroll position at the last show-or-hide and
         * at the last frame. They are locals, not state: state would re-render on
         * every frame, and the update would not be readable until the next render.
         * hiddenRef mirrors the class so an unchanged frame does not setState.
         */
        let frame = 0;
        let anchor = 0;
        let previous = 0;
        let cancelled = false;
        const readScrollY = () => {
            const root = document.scrollingElement ?? document.documentElement;
            const max = Math.max(0, root.scrollHeight - root.clientHeight);
            return Math.min(max, Math.max(0, window.scrollY));
        };
        const commit = (next) => {
            if (hiddenRef.current === next)
                return;
            hiddenRef.current = next;
            setHidden(next);
        };
        const onFrame = () => {
            frame = 0;
            if (cancelled || !media.matches)
                return;
            const y = readScrollY();
            if (y < ALWAYS_SHOWN_BELOW_PX || y < previous) {
                anchor = y;
                commit(false);
            }
            else if (y - anchor > HIDE_AFTER_PX) {
                anchor = y;
                commit(true);
            }
            previous = y;
        };
        const onScroll = () => {
            if (frame !== 0)
                return;
            frame = window.requestAnimationFrame(onFrame);
        };
        const stop = () => {
            window.removeEventListener("scroll", onScroll);
            if (frame !== 0)
                cancelAnimationFrame(frame);
            frame = 0;
        };
        const start = () => {
            const y = readScrollY();
            anchor = y;
            previous = y;
            window.addEventListener("scroll", onScroll, { passive: true });
        };
        const onMediaChange = () => {
            stop();
            if (media.matches)
                start();
            else
                commit(false);
        };
        commit(false);
        if (media.matches)
            start();
        media.addEventListener("change", onMediaChange);
        return () => {
            cancelled = true;
            media.removeEventListener("change", onMediaChange);
            stop();
        };
    }, [location.pathname]);
    /*
     * UserMenu owns the label, including the NRE "own org / represented IM
     * (code)" wording, and that file is shared with the legacy header. The
     * tooltip is written onto the toggle after commit so it matches the text
     * that was actually rendered. The accessible name stays that text; title
     * is only the hover tooltip.
     */
    useEffect(() => {
        const toggle = userMenuRef.current?.querySelector(".rs-dropdown-toggle");
        if (!(toggle instanceof HTMLElement))
            return;
        const text = (toggle.textContent ?? "").replace(/\s+/g, " ").trim();
        if (text)
            toggle.title = text;
        else
            toggle.removeAttribute("title");
    }, [isAuthenticated, activeRole, userRolesCompany, enabledExtras]);
    const envStyle = getEnvStyle();
    return (React.createElement(Header, { className: hidden
            ? "redesign-shell-header redesign-shell-header-hidden"
            : "redesign-shell-header" },
        React.createElement("div", { className: "redesign-shell-brand" },
            React.createElement("a", { href: "/", className: "redesign-shell-logo-link" },
                React.createElement("img", { src: eraLogoPath, alt: "ERA" })),
            React.createElement("span", { className: "redesign-shell-wordmark" },
                React.createElement("span", null,
                    "RINF",
                    React.createElement("span", { className: "redesign-shell-wordmark-plus" }, "+")))),
        envStyle && (React.createElement("span", { role: "status", "aria-label": `${envStyle.label} environment`, className: "redesign-shell-env-badge", style: {
                backgroundColor: envStyle.bg,
                color: envStyle.fg,
            } }, envStyle.label)),
        React.createElement("div", { className: "redesign-shell-header-end" },
            React.createElement("span", { role: "img", "aria-label": "Help", className: "redesign-shell-header-help" },
                React.createElement("svg", { width: "18", height: "18", viewBox: "0 0 18 18", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true" },
                    React.createElement("circle", { cx: "9", cy: "9", r: "7" }),
                    React.createElement("path", { d: "M6.82 6.75a2.25 2.25 0 0 1 4.37.75c0 1.5-2.25 2.25-2.25 2.25" }),
                    React.createElement("path", { d: "M9 13.5h.01" }))),
            activeRole?.role && (React.createElement("span", { className: "redesign-shell-role-badge" }, activeRole.role.toUpperCase())),
            enabledExtras.includes("userLogin") && (React.createElement("div", { ref: userMenuRef, className: auth && userInitials
                    ? "redesign-shell-user-pill redesign-shell-user-pill-with-avatar"
                    : "redesign-shell-user-pill" },
                auth && userInitials && (React.createElement("span", { className: "redesign-shell-user-initials", "aria-hidden": "true" }, userInitials)),
                React.createElement(Nav, null,
                    React.createElement(UserMenu, { isAuthenticated: isAuthenticated, userInfo: userInfo, userRoles: userRoles, activeRole: activeRole, setRole: setRole, userRolesCompany: userRolesCompany })))),
            menuButton)));
}
